import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  EXEC_CHANNEL,
  EXEC_SESSION_SUBPROTOCOL,
  type ExecResizeFrame,
  type ExecSessionCapability,
  type ExecSessionRequest,
  type ExecSessionStatus,
  type ExecSessionKeepaliveResponse,
  type ExecSessionTicket,
  type ExecTranscriptResponse,
} from "@k8s-dashboard/contracts";
import { apiGet, apiRequest, clusterPath, currentScenarioParam } from "./client";

/**
 * Debug Terminal 클라이언트 (ADR 0022 · Issue #10)
 * --------------------------------------------------------------------------
 * 브라우저는 명령을 만들지 않습니다. 서버가 소유한 shell profile을 고를 뿐이고,
 * 세션 승급은 POST가 내려준 **Path 제한 단발성 쿠키 하나**로만 일어납니다.
 * 그래서 이 모듈은 어떤 토큰도 URL·query에 싣지 않습니다 — 실을 값 자체가 없습니다.
 */

const base = (clusterId: string, podName: string) =>
  clusterPath(clusterId, "pods", podName, "debug-session");

/** 진입 상태. Pod UID가 캐시 키입니다 — 이름은 신원이 아닙니다. */
export function useExecCapability(clusterId: string, ns: string, podName: string, podUid: string, enabled: boolean) {
  return useQuery({
    queryKey: ["exec-session", clusterId, ns, podUid],
    enabled: enabled && Boolean(clusterId && ns && podName && podUid),
    retry: false,
    staleTime: 10_000,
    queryFn: ({ signal }) => apiGet<ExecSessionCapability>(base(clusterId, podName), { ns, uid: podUid }, signal),
  });
}

/**
 * 세션 자격을 발급받습니다. 응답 본문에는 credential이 없고, 실제 승급은 같은
 * 응답의 Set-Cookie 하나입니다. 그 쿠키는 attachPath에만 붙고 한 번 쓰면 사라집니다.
 */
export function createExecSession(
  clusterId: string,
  ns: string,
  podName: string,
  podUid: string,
  request: ExecSessionRequest,
): Promise<ExecSessionTicket> {
  const params = new URLSearchParams({ ns, uid: podUid });
  for (const [key, value] of currentScenarioParam()) params.set(key, value);
  return apiRequest<ExecSessionTicket>(`${base(clusterId, podName)}?${params}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(request),
  });
}

/**
 * 열린 세션 하나를 다루는 얇은 손잡이입니다.
 *
 * 자동 재접속이 **없습니다.** 다시 붙으려면 새 자격을 발급받아야 하고, 그 사이에
 * Pod가 교체되었으면 서버가 거절합니다 — 자동 재시도는 재생성 Pod 오접속과
 * 구분되지 않습니다.
 */
export interface ExecSocket {
  send(text: string): void;
  resize(size: ExecResizeFrame): void;
  close(): void;
  readonly ready: boolean;
}

export interface ExecSocketHandlers {
  /**
   * WebSocket이 **실제로 열렸습니다.** 이 신호 전에는 세션이 열린 것이 아닙니다.
   *
   * `new WebSocket(...)`이 돌아왔다는 것은 "요청을 보냈다"는 뜻일 뿐입니다. 서버가
   * 승급을 거절하면 onopen 없이 onclose만 옵니다. 그 사이를 "열림"으로 취급하면
   * 그때 보낸 입력은 조용히 사라지고, 거절은 평범한 종료로 보입니다.
   */
  onOpen: () => void;
  /** stdout과 stderr을 한 흐름으로 넘깁니다 — TTY 세션에서 원격도 그렇게 합칩니다. */
  onOutput: (chunk: string) => void;
  onStatus: (status: ExecSessionStatus) => void;
  /** 소켓이 닫혔습니다. `opened`는 onopen을 한 번이라도 지났는지입니다. */
  onClosed: (opened: boolean) => void;
}

/**
 * attachPath로 WebSocket을 엽니다.
 *
 * subprotocol을 반드시 요청합니다 — 서버는 이 값이 없으면 승격하지 않습니다.
 * URL에는 query가 붙지 않습니다.
 */
export function openExecSocket(attachPath: string, handlers: ExecSocketHandlers): ExecSocket {
  const url = new URL(attachPath, window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  const ws = new WebSocket(url.toString(), EXEC_SESSION_SUBPROTOCOL);
  ws.binaryType = "arraybuffer";
  const decoder = new TextDecoder();
  const encoder = new TextEncoder();
  let opened = false;

  ws.onopen = () => {
    opened = true;
    handlers.onOpen();
  };

  ws.onmessage = (event) => {
    if (!(event.data instanceof ArrayBuffer)) return;
    const bytes = new Uint8Array(event.data);
    if (!bytes.length) return;
    const body = decoder.decode(bytes.subarray(1));
    if (bytes[0] === EXEC_CHANNEL.stdout || bytes[0] === EXEC_CHANNEL.stderr) handlers.onOutput(body);
    else if (bytes[0] === EXEC_CHANNEL.status) {
      // 서버가 소유한 문구를 지어내지 않습니다. 깨진 프레임이면 닫힘만 알립니다.
      try {
        handlers.onStatus(JSON.parse(body) as ExecSessionStatus);
      } catch {
        /* 무시 — onClosed가 이어집니다. */
      }
    }
    /* 모르는 채널은 무시합니다. 서버가 보내지 않는 값이고, 여기서 세션을 죽이면
       채널을 더할 때마다 구 클라이언트가 끊깁니다. */
  };
  ws.onclose = () => handlers.onClosed(opened);

  const send = (channel: number, body: Uint8Array) => {
    if (ws.readyState !== WebSocket.OPEN) return;
    const out = new Uint8Array(body.length + 1);
    out[0] = channel;
    out.set(body, 1);
    ws.send(out);
  };
  return {
    send: (text) => send(EXEC_CHANNEL.stdin, encoder.encode(text)),
    resize: (size) => send(EXEC_CHANNEL.resize, encoder.encode(JSON.stringify(size))),
    close: () => ws.close(),
    get ready() {
      return opened && ws.readyState === WebSocket.OPEN;
    },
  };
}

/**
 * 세션 기록(ADR 0031). 페이지는 서버가 준 `nextCursor`를 그대로 되돌려 보내는 cursor 방식이고,
 * offset은 없습니다. 권한 없음·기록 없음·꺼진 배포는 전부 서버 문구가 실린 404라 재시도하지 않습니다.
 */
export function useExecTranscript(sessionId: string | null) {
  return useInfiniteQuery({
    queryKey: ["exec-transcript", sessionId],
    enabled: Boolean(sessionId),
    initialPageParam: "",
    queryFn: ({ pageParam, signal }) =>
      apiGet<ExecTranscriptResponse>(
        `/api/v1/exec-sessions/${encodeURIComponent(sessionId ?? "")}/transcript`,
        pageParam ? { cursor: pageParam } : {},
        signal,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    retry: false,
    staleTime: 5_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * 세션을 붙잡고 있는 인증의 만료를 다시 씁니다 (ADR 0032).
 *
 * 값은 보내지 않습니다 — 이 요청을 인증한 자격에서 **서버가** 읽습니다. 그래서 브라우저가
 * 자기 토큰을 갱신해 로그인 상태를 유지하는 동안 세션이 살고, 로그아웃하면 마지막으로
 * 기록된 만료에서 끝납니다. 실패는 조용히 넘깁니다 — 갱신하지 못하면 세션이 그 시각에
 * 끝날 뿐이고, 그것이 안전한 쪽입니다.
 */
export function keepaliveExecSession(sessionId: string): Promise<ExecSessionKeepaliveResponse> {
  return apiRequest<ExecSessionKeepaliveResponse>(`/api/v1/exec-sessions/${encodeURIComponent(sessionId)}/keepalive`, { method: "POST" });
}

/**
 * 이 탭에서 열려 있는 세션 id입니다(ADR 0030 결정 4). 로그아웃할 때 이 목록의 세션을 best effort로
 * 끝냅니다 — 인증이 사라진 뒤까지 shell이 남지 않게 합니다. 실패해도 세션은 인증 만료·유휴 상한에서
 * 끝나므로 로그아웃을 막지 않습니다.
 */
const openSessions = new Set<string>();

export function trackOpenExecSession(sessionId: string): () => void {
  openSessions.add(sessionId);
  return () => openSessions.delete(sessionId);
}

/**
 * 열린 세션을 끝냅니다. **토큰을 버리기 전에** 불러야 합니다 — DELETE도 인증이 필요합니다.
 * 기다리는 시간에 상한을 둡니다: 로그아웃이 세션 종료를 기다리며 멈추면 안 됩니다.
 */
export async function terminateOpenExecSessions(timeoutMs = 1_500): Promise<void> {
  if (openSessions.size === 0) return;
  const ids = [...openSessions];
  openSessions.clear();
  const all = Promise.allSettled(
    ids.map((id) => apiRequest<void>(`/api/v1/exec-sessions/${encodeURIComponent(id)}`, { method: "DELETE", keepalive: true })),
  );
  await Promise.race([all, new Promise((resolve) => window.setTimeout(resolve, timeoutMs))]);
}
